import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { f2 } from '../copyFlow2'
import { IconButton } from './Button'
import { Close } from './icons'
import { FADE, spring } from './motion'
import { useDesktop } from './useMedia'
import { useVisibleBottom } from './useVisibleBottom'

type Props = { open: boolean; onClose: () => void; label: string; title?: ReactNode; footer?: ReactNode; width?: number; children: ReactNode; reviewState?: string }

/**
 * Editor surface (Astra Flow 2): a full-width sheet on phones whose top edge stays at or below y = 111, a centered
 * dialog on desktop. Content scrolls inside; an optional footer stays pinned. Escape and the scrim close it.
 */
export function Modal({ open, onClose, label, title, footer, width = 480, children, reviewState }: Props) {
  const desktop = useDesktop()
  const reduce = useReducedMotion() ?? false
  // B-147: on a phone the sheet stands on what the person can see (above a browser toolbar or the keyboard), and its
  // height uses the small viewport (svh: toolbars shown), so the footer's buttons are always on screen
  const covered = useVisibleBottom(open && !desktop)
  const panel = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => panel.current?.focus({ preventScroll: true }))
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus?.({ preventScroll: true })
    }
  }, [open])

  const enter = reduce ? { opacity: 0 } : desktop ? { opacity: 0, scale: 0.97 } : { y: '100%' }
  const shown = reduce ? { opacity: 1 } : desktop ? { opacity: 1, scale: 1 } : { y: 0 }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className={`fixed inset-0 z-50 flex ${desktop ? 'items-center justify-center p-8' : 'items-end'}`} style={covered ? { bottom: covered } : undefined}>
          <motion.div
            aria-hidden="true"
            className="absolute inset-0 bg-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: FADE.enter }}
            exit={{ opacity: 0, transition: FADE.exit }}
            onClick={() => closeRef.current()}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            data-review-state={reviewState}
            tabIndex={-1}
            className={`relative flex w-full flex-col bg-surface shadow-card outline-none ${
              desktop ? 'max-h-[min(760px,calc(100dvh-64px))] rounded-[24px]' : 'rounded-t-[28px]'
            }`}
            // phone: the top edge stays at or below y = 111 (64 on a short screen, so a 667 px phone keeps room for the list)
            style={desktop ? { maxWidth: width } : { maxHeight: `calc(100svh - ${typeof window !== 'undefined' && window.innerHeight < 720 ? 64 : 111}px - ${covered}px)` }}
            initial={enter}
            animate={shown}
            exit={{ ...enter, transition: reduce ? FADE.exit : spring('sheet') }}
            transition={reduce ? FADE.enter : spring('sheet')}
          >
            <div className="flex min-h-[52px] shrink-0 items-center gap-2 pl-5 pr-2 pt-2 lg:pl-8 lg:pr-4 lg:pt-4">
              <div className="min-w-0 flex-1">{title}</div>
              <IconButton label={f2('common.close')} onClick={() => closeRef.current()}>
                <Close size={20} />
              </IconButton>
            </div>
            <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 lg:px-8 ${footer ? 'pb-4' : desktop ? 'pb-6' : 'pb-[calc(env(safe-area-inset-bottom)+20px)]'}`}>{children}</div>
            {footer && (
              <div className={`shrink-0 border-t border-line px-5 pt-3 lg:px-8 ${desktop ? 'pb-5' : 'pb-[calc(env(safe-area-inset-bottom)+12px)]'}`}>{footer}</div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
